import { SpanStatusCode, trace, Tracer } from "@opentelemetry/api";
import { Chart, StudyTemplates } from "../models/charts";

var tracer: Tracer;

function initTracer() {
    tracer = trace.getTracer("vortex-tracer");
}
export class SaveLoad {
    private _charts: Chart[] = [];
    private _studyTemplates: StudyTemplates[] = [];
    private _drawingTemplates: StudyTemplates[] = [];
    private _inHouseUrl: string;
    private _auth_token: string;
    private _getStudyTemplates: Promise<StudyTemplates[]>;
    private _getDrawingTemplates: Promise<StudyTemplates[]>;
    // Last-resort symbol used to repair a saved layout whose symbol is empty/"-"
    // and that cannot be recovered from the layout's own data. flow-web boots via
    // load_last_chart with no per-open symbol, so the caller passes a sensible
    // default (NSE:NIFTYIDX) to guarantee a corrupt layout never hangs on the
    // loading spinner. Null when no usable fallback was provided.
    private _fallbackSymbol: string | null;
    constructor(inHouseUrl: string, auth_token: string, fallbackSymbol?: string) {
        this.init();
        tracer = tracer;
        this._charts = [];
        this._studyTemplates = [];
        this._drawingTemplates = [];
        this._inHouseUrl = inHouseUrl + "aux/";
        this._auth_token = auth_token;
        this._fallbackSymbol = (typeof fallbackSymbol === "string" && fallbackSymbol.trim() !== "" && fallbackSymbol !== "-")
            ? fallbackSymbol
            : null;
        this._getStudyTemplates = this._getAllStudyTemplates();
        this._getDrawingTemplates = this._getAllDrawingTemplates();
    }

    init() {
        initTracer()
    }

    getAllCharts(): Promise<Chart[]> {
        return tracer.startActiveSpan("GetAllCharts", async (span) => {
            return this._getAllCharts().then((_) => {
                return Promise.resolve(this._charts);
            }).catch((error) => {
                span.setStatus({ code: SpanStatusCode.ERROR });
                return Promise.reject(error)
            }).finally(() => {
                span.end()
            })
        })

    }

    _getAllCharts() {
        return this._inhouseRequest('chartsList').then((response: Chart[]) => {
            const raw = (response || [])
            // Drop layouts that are corrupt AND unrepairable so they can't be picked
            // from the "Load layout" list. List entries that arrive without content
            // can't be judged here, so keep them — repair still runs on getChartContent.
            this._charts = raw.filter((c: Chart) => {
                if (!c || typeof c.id === 'undefined') { return false }
                if (typeof c.content !== 'string' || c.content.trim() === '') { return true }
                return this._hasUsableContent(c) || this._canRepair(c)
            })
            return Promise.resolve()
        })
    }

    removeChart(id: number): Promise<void> {
        return tracer.startActiveSpan("RemoveChart", async (span) => {
            for (var i = 0; i < this._charts.length; ++i) {
                if (this._charts[i].id.toString() === id.toString()) {

                    return this._inhouseRequest('charts/' + id.toString(), undefined, undefined, "DELETE").then((_) => {
                        this._charts.splice(i, 1);
                        return Promise.resolve()
                    }).catch((err) => { return Promise.reject(err) })
                        .finally(() => {
                            span.end()
                        })
                }
            }
            span.end()
            return Promise.reject();
        })

    }

    saveChart(chartData: Chart): Promise<string> {
        chartData.timestamp = new Date().valueOf();
        return tracer.startActiveSpan("SaveChart", async (span) => {
            // Root-cause guard: never persist a layout whose symbol is empty/"-" at
            // the wrapper or on any MainSeries. TV autosave can fire mid symbol-change/
            // resolve with a transient empty symbol — that is how the corrupt rows were
            // generated. Resolve (not reject) with no id so TV moves on and retries
            // once the symbol is valid again.
            if (!this._isValidForSave(chartData)) {
                span.addEvent("chart.save_skipped_invalid_symbol", { symbol: chartData.symbol ?? "" });
                span.end();
                return Promise.resolve("");
            }
            return this._inhouseRequest('charts', chartData, undefined, "POST").then((response: Chart) => {
                var index: number = this._charts.findIndex((t: Chart) => t.id == chartData.id)
                if (index == -1) {
                    window.GtmPusher({
                        event: 'tv-chart-layout-created'
                    })
                    this._charts.push(response);
                } else {
                    this._charts[index] = response
                }

                return Promise.resolve(response.id.toString());
            }).catch((error) => { console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error) })
                .finally(() => {
                    span.end()
                })
        })

    }

    getChartContent(id: number): Promise<string> {
        return tracer.startActiveSpan("GetChartContent", async (span) => {
            return this._inhouseRequest('charts/' + id.toString(), undefined, undefined, "GET").then((response: Chart) => {
                // Happy path: the wrapper already has a usable symbol.
                if (this._hasUsableContent(response)) {
                    return Promise.resolve(response.content);
                }
                // Corrupt symbol: repair from the layout's own data, then the fallback.
                const repaired = this._tryRepair(response);
                if (repaired !== null) {
                    span.addEvent("chart.repaired_on_load", { chartId: id });
                    // Signal the container that THIS load was a repaired layout. Its saved
                    // price range is untrustworthy (auto-scale may be off with a stale/locked
                    // range from the corrupt state), so the chart can re-fit the Y axis once
                    // on first data load instead of the user having to click "Auto".
                    if (typeof window !== "undefined") { (window as any).__chartLayoutRepaired = true; }
                    return Promise.resolve(repaired);
                }
                // Unrepairable: reject cleanly so TV fails fast to a default chart
                // instead of hanging forever on the loading spinner.
                span.setStatus({ code: SpanStatusCode.ERROR, message: "invalid_chart_content" });
                return Promise.reject("invalid_chart_content");
            }).catch((error) => { console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error) })
                .finally(() => {
                    span.end()
                })
        })
    }

    // ----- corrupt-chart-JSON repair helpers ---------------------------------
    // A saved layout's `content` is the TradingView "wrapper" (symbol envelope plus
    // a double-encoded inner layout string). Corruption seen in prod = the symbol
    // goes empty/"-", which leaves TV stuck on the loading spinner. These helpers
    // detect that, recover a symbol from up to four places, and repair or drop it.

    private _isNonEmptySymbol(s: unknown): s is string {
        return typeof s === "string" && s.trim() !== "" && s !== "-";
    }

    // chart.content -> wrapper object (outer envelope).
    private _parseWrapper(chart: Chart | null | undefined): any {
        if (!chart || typeof chart.content !== "string" || chart.content.trim() === "") { return null; }
        try { return JSON.parse(chart.content); } catch { return null; }
    }

    // wrapper.content (double-encoded) -> inner TradingView layout root.
    private _parseInnerLayout(wrapper: any): any {
        if (!wrapper || typeof wrapper.content !== "string" || wrapper.content.trim() === "") { return null; }
        try { return JSON.parse(wrapper.content); } catch { return null; }
    }

    // Walk every MainSeries state across ALL charts and panes (not just the first),
    // so multi-chart / multi-pane layouts are covered too.
    private _forEachMainSeriesState(layout: any, fn: (state: any) => void): void {
        const charts = layout && layout.charts;
        if (!Array.isArray(charts)) { return; }
        for (const c of charts) {
            const panes = c && c.panes;
            if (!Array.isArray(panes)) { continue; }
            for (const pane of panes) {
                const sources = pane && pane.sources;
                if (!Array.isArray(sources)) { continue; }
                for (const src of sources) {
                    if (src && src.type === "MainSeries" && src.state) { fn(src.state); }
                }
            }
        }
    }

    private _hasUsableContent(chart: Chart): boolean {
        const wrapper = this._parseWrapper(chart);
        return wrapper !== null && this._isNonEmptySymbol(wrapper.symbol);
    }

    private _canRepair(chart: Chart): boolean {
        const wrapper = this._parseWrapper(chart);
        return wrapper !== null && this._findRecoverySymbol(wrapper) !== null;
    }

    // Recovery order: wrapper.short_name -> wrapper.charts_symbols -> inner MainSeries
    // (symbol, then shortName; any chart/pane) -> constructor fallback (NSE:NIFTYIDX).
    private _findRecoverySymbol(wrapper: any): string | null {
        if (!wrapper) { return null; }
        if (this._isNonEmptySymbol(wrapper.short_name)) { return wrapper.short_name; }

        const fromMap = this._symbolFromChartsSymbols(wrapper.charts_symbols);
        if (this._isNonEmptySymbol(fromMap)) { return fromMap; }

        const layout = this._parseInnerLayout(wrapper);
        let recovered: string | null = null;
        if (layout) {
            this._forEachMainSeriesState(layout, (state) => {
                if (recovered) { return; }
                if (this._isNonEmptySymbol(state.symbol)) { recovered = state.symbol; }
                else if (this._isNonEmptySymbol(state.shortName)) { recovered = state.shortName; }
            });
        }
        if (recovered) { return recovered; }

        return this._fallbackSymbol;
    }

    // wrapper.charts_symbols is a stringified map, e.g. {"1":{"symbol":"NSE:IDEA"}}.
    private _symbolFromChartsSymbols(raw: unknown): string | null {
        if (typeof raw !== "string" || raw.trim() === "") { return null; }
        try {
            const map = JSON.parse(raw);
            if (map && typeof map === "object") {
                for (const key of Object.keys(map)) {
                    const entry = (map as any)[key];
                    if (entry && this._isNonEmptySymbol(entry.symbol)) { return entry.symbol; }
                }
            }
        } catch { /* ignore */ }
        return null;
    }

    // Returns a repaired wrapper string, or null if no symbol can be recovered.
    private _tryRepair(chart: Chart): string | null {
        const wrapper = this._parseWrapper(chart);
        if (!wrapper) { return null; }
        const recovered = this._findRecoverySymbol(wrapper);
        if (!recovered) { return null; }

        if (!this._isNonEmptySymbol(wrapper.symbol)) { wrapper.symbol = recovered; }

        const layout = this._parseInnerLayout(wrapper);
        if (layout) {
            let mutated = false;
            this._forEachMainSeriesState(layout, (state) => {
                if (!this._isNonEmptySymbol(state.symbol)) { state.symbol = recovered; mutated = true; }
            });
            if (mutated) {
                try { wrapper.content = JSON.stringify(layout); } catch { /* leave inner untouched */ }
            }
        }
        try { return JSON.stringify(wrapper); } catch { return null; }
    }

    // A layout is safe to persist only if the wrapper symbol AND every MainSeries
    // symbol are non-empty. NOTE: the wrapper's `content` is double-encoded, so this
    // parses one level deeper than the mobile_charts original (which checked the
    // wrapper level and so never actually validated the inner series).
    private _isValidForSave(chartData: Chart): boolean {
        if (!chartData) { return false; }
        if (!this._isNonEmptySymbol(chartData.symbol)) { return false; }
        const wrapper = this._parseWrapper(chartData);
        if (wrapper) {
            if (wrapper.symbol !== undefined && !this._isNonEmptySymbol(wrapper.symbol)) { return false; }
            const layout = this._parseInnerLayout(wrapper);
            if (layout) {
                let invalid = false;
                this._forEachMainSeriesState(layout, (state) => {
                    if (state.symbol !== undefined && !this._isNonEmptySymbol(state.symbol)) { invalid = true; }
                });
                if (invalid) { return false; }
            }
        }
        return true;
    }
    // ----- end corrupt-chart-JSON repair helpers -----------------------------

    removeStudyTemplate(studyTemplateInfo: StudyTemplates): Promise<void> {
        console.error("remove study template", studyTemplateInfo)
        var id = studyTemplateInfo.name?.split("|")[0]
        for (var i = 0; i < this._studyTemplates.length; ++i) {
            if (this._studyTemplates[i].id?.toString() === id?.toString()) {
                return tracer.startActiveSpan("RemoveStudyTemplate", async (span) => {
                    return this._inhouseRequest('studyTemplates/' + id?.toString(), undefined, undefined, "DELETE").then((_) => {
                        this._studyTemplates.splice(i, 1);
                        return Promise.resolve()
                    }).catch((err) => { return Promise.reject(err) })
                        .finally(() => {
                            span.end()
                        })
                })
            }
        }

        return Promise.reject();
    }

    getStudyTemplateContent(studyTemplateData: StudyTemplates): Promise<string> {
        for (var i = 0; i < this._studyTemplates.length; ++i) {
            if (this._studyTemplates[i].name === studyTemplateData.name) {
                if (this._studyTemplates[i] !== undefined) {
                    return Promise.resolve(this._studyTemplates[i].content);
                } else {
                    return Promise.reject();
                }

            }
        }

        return Promise.reject();
    }

    saveStudyTemplate(studyTemplateData: StudyTemplates): Promise<void> {
        console.error("study template", studyTemplateData)

        studyTemplateData.timestamp = new Date().valueOf();
        var aa = JSON.stringify(studyTemplateData.meta_info)
        studyTemplateData.meta_info = aa
        return tracer.startActiveSpan("async ", async (span) => {
            return this._inhouseRequest('studyTemplates', studyTemplateData, undefined, "POST").then((response: StudyTemplates) => {
                response.name = response.id?.toString() + "|" + response.name
                var index: number = this._studyTemplates.findIndex((t: StudyTemplates) => t.id == studyTemplateData.id)
                if (index == -1) {
                    this._studyTemplates.push(response);
                } else {
                    this._studyTemplates[index] = response
                }
                return Promise.resolve();
            }).catch((error) => { console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error) })
                .finally(() => {
                    span.end()
                })
        })
    }

    getAllStudyTemplates(): Promise<StudyTemplates[]> {
        return this._getStudyTemplates.then((_) => {
            return Promise.resolve(this._studyTemplates);
        })

    }

    _getAllStudyTemplates() {
        return tracer.startActiveSpan("GetAllStudyTemplates", async (span) => {
            return this._inhouseRequest('studyTemplates').then((response: StudyTemplates[]) => {
                this._studyTemplates = (response.map((aa: StudyTemplates) => {
                    aa.name = aa.id?.toString() + "|" + aa.name
                    aa.meta_info = JSON.parse(aa.meta_info as string)
                    return aa
                }) || [])
                return Promise.resolve(this._studyTemplates)
            }).catch((error) => { console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error) })
                .finally(() => {
                    span.end()
                })
        })

    }

    _getAllDrawingTemplates() {
        return tracer.startActiveSpan("GetAllDrawingTemplates", async (span) => {
            return this._inhouseRequest('drawingTemplates').then((response: StudyTemplates[]) => {
                this._drawingTemplates = (response.map((aa: StudyTemplates) => {
                    aa.name = aa.name
                    aa.content = aa.content
                    return aa
                }) || [])
                return Promise.resolve(this._drawingTemplates)
            }).catch((error) => { console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error) })
                .finally(() => {
                    span.end()
                })
        })
    }
    removeDrawingTemplate(templateName: string): Promise<void> {
        for (var i = 0; i < this._drawingTemplates.length; ++i) {
            if (this._drawingTemplates[i].name === templateName) {
                return tracer.startActiveSpan("RemoveDrawingTemplate", async (span) => {
                    return this._inhouseRequest(`drawingTemplates/${this._drawingTemplates[i].id}`, undefined, undefined, "DELETE").then((_) => {
                        this._drawingTemplates.splice(i, 1);
                        return Promise.resolve();
                    }).catch((err) => { return Promise.reject(err) })
                        .finally(() => {
                            span.end()
                        })
                })
            }
        }

        return Promise.reject();
    }

    loadDrawingTemplate(templateName: string): Promise<string> {
        for (var i = 0; i < this._drawingTemplates.length; ++i) {
            if (this._drawingTemplates[i].name === templateName) {
                return Promise.resolve(this._drawingTemplates[i].content);
            }
        }

        return Promise.reject();
    }

    saveDrawingTemplate(templateName: string, content: string): Promise<void> {
        // studyTemplateData.meta_info = JSON.stringify(studyTemplateData.meta_info)
        var payload: StudyTemplates = {
            name: templateName,
            content: content,
            id: 0
        }
        for (var i = 0; i < this._drawingTemplates.length; ++i) {
            if (this._drawingTemplates[i].name === templateName) {
                payload.id = this._drawingTemplates[i].id
                break;
            }
        }
        return tracer.startActiveSpan("SaveDrawingTemplate", async (span) => {
            return this._inhouseRequest('drawingTemplates', payload, undefined, "POST").then((response) => {
                response.name = payload.name
                response.id = response.drawing_template_id
                for (var i = 0; i < this._drawingTemplates.length; ++i) {
                    if (this._drawingTemplates[i].name === templateName) {
                        this._drawingTemplates.splice(i, 1);
                        break;
                    }
                }
                this._drawingTemplates.push(response);
                return Promise.resolve();
            }).catch((error) => { console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error) })
                .finally(() => {
                    span.end()
                })
        })

    }

    getDrawingTemplates(): Promise<string[]> {
        return this._getDrawingTemplates.then((_) => {
            return Promise.resolve(this._drawingTemplates.map(function (template: StudyTemplates) {
                return template.name;
            }));
        })
    }
    private async _inhouseRequest(urlPath: string, body?: StudyTemplates, params?: Record<string, string>, method: string = "GET") {
        const headers = new Headers({
            "Authorization": `Bearer ${this._auth_token}`,
        });

        const options: RequestInit = {
            headers,
            method,
        };

        if (body !== undefined && method !== "GET") {
            options.body = JSON.stringify(body);
        }

        const url = new URL(`${this._inHouseUrl}${urlPath}`);
        if (params) {
            url.search = new URLSearchParams(params).toString();
        }

        const response = await fetch(url.toString(), options);
        const responseText = await response.text();
        return JSON.parse(responseText);
    }

}